import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProvider } from '../../contexts/ProviderContext';
import { useToast } from '../../contexts/ToastContext';
import { supabase } from '../../lib/supabase';
import {
  fetchProviderOrders,
  ProviderOrder,
  formatCurrency,
  formatDate,
  getStatusColor,
} from '../../lib/providerOrders';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import ProviderLayout from './ProviderLayout';

const ProviderOrdersList: React.FC = () => {
  const navigate = useNavigate();
  const { currentProviderId } = useProvider();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<ProviderOrder[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMerchant, setSelectedMerchant] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);

  // Merchants list for filter
  const [merchants, setMerchants] = useState<Array<{ id: string; name: string; shop_domain: string }>>([]);

  useEffect(() => {
    if (currentProviderId) {
      loadMerchants();
      loadOrders();
    }
  }, [currentProviderId, currentPage, selectedMerchant, selectedStatus, dateFrom, dateTo]);

  const loadMerchants = async () => {
    if (!currentProviderId) return;

    try {
      // Get unique merchants from provider_orders_view
      const { data, error } = await supabase
        .from('provider_orders_view')
        .select('merchant_id, merchant_name, merchant_shop_domain')
        .eq('provider_id', currentProviderId);

      if (error) throw error;

      // Remove duplicates
      const uniqueMerchants = Array.from(
        new Map(
          data?.map((item) => [item.merchant_id, {
            id: item.merchant_id,
            name: item.merchant_name,
            shop_domain: item.merchant_shop_domain,
          }])
        ).values()
      );

      setMerchants(uniqueMerchants);
    } catch (err) {
      console.error('Error loading merchants:', err);
    }
  };

  const loadOrders = async () => {
    if (!currentProviderId) return;

    try {
      setLoading(true);
      const result = await fetchProviderOrders({
        provider_id: currentProviderId,
        merchant_id: selectedMerchant || undefined,
        status: selectedStatus || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
      });
      setOrders(result.orders);
      setTotalCount(result.total_count);
    } catch (err) {
      console.error('Error loading orders:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedMerchant('');
    setSelectedStatus('');
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
  };

  const getStatusBadge = (order: ProviderOrder) => {
    const status = order.latest_provider_update?.status || order.display_fulfillment_status || 'pending';
    const color = getStatusColor(status);
    const colorClasses: Record<string, string> = {
      emerald: 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20',
      yellow: 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20',
      blue: 'bg-blue-500/10 text-blue-400 border border-blue-500/20',
      indigo: 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20',
      purple: 'bg-purple-500/10 text-purple-400 border border-purple-500/20',
      green: 'bg-green-500/10 text-green-400 border border-green-500/20',
      red: 'bg-red-500/10 text-red-400 border border-red-500/20',
      orange: 'bg-orange-500/10 text-orange-400 border border-orange-500/20',
      gray: 'bg-white/[0.06] text-white/50 border border-white/15',
    };

    // Format status for display (replace underscores with spaces)
    const displayStatus = status.replace(/_/g, ' ').toUpperCase();

    return (
      <span className={`px-2.5 py-1 text-xs  font-semibold rounded-full ${colorClasses[color] || colorClasses.gray}`}>
        {displayStatus}
      </span>
    );
  };

  // Filter orders by search term (client-side filtering)
  const filteredOrders = orders.filter((order) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      order.order_number?.toLowerCase().includes(search) ||
      order.merchant_shop_name?.toLowerCase().includes(search) ||
      order.customer_email?.toLowerCase().includes(search) ||
      order.external_order_id?.toLowerCase().includes(search)
    );
  });

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  return (
    <ProviderLayout backTo="/provider/orders">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white ">ALL ORDERS</h1>
          <p className="text-white/50 mt-2">
            Showing {filteredOrders.length} of {totalCount} orders
          </p>
        </div>

        {/* Search and Filters */}
        <div className=" rounded-lg shadow border border-white/[0.08] mb-6">
          <div className="p-4">
            {/* Search Bar */}
            <div className="mb-4">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/25" />
                <input
                  type="text"
                  placeholder="Search by order #, merchant, customer email..."
                  className="w-full pl-10 pr-4 py-3 border border-white/15 rounded  text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-orange-500  text-sm"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Filter Toggle Button */}
            <div className="flex items-center gap-2 mb-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-transparent text-white/70 rounded  text-sm hover:bg-white/[0.06] transition-colors"
              >
                <FunnelIcon className="w-4 h-4" />
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </button>

              {(selectedMerchant || selectedStatus || dateFrom || dateTo) && (
                <button
                  onClick={resetFilters}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg text-sm hover:bg-red-500/20 transition-colors"
                >
                  <XMarkIcon className="w-4 h-4" />
                  Clear Filters
                </button>
              )}
            </div>

            {/* Filters Panel */}
            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-white/[0.08]">
                {/* Merchant Filter */}
                <div>
                  <label className="block text-sm  font-semibold text-white/70 mb-2">
                    MERCHANT
                  </label>
                  <select
                    value={selectedMerchant}
                    onChange={(e) => {
                      setSelectedMerchant(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full px-3 py-2 border border-white/15 rounded  text-white  text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All Merchants</option>
                    {merchants.map((merchant) => (
                      <option key={merchant.id} value={merchant.id}>
                        {merchant.name || merchant.shop_domain}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Status Filter */}
                <div>
                  <label className="block text-sm  font-semibold text-white/70 mb-2">
                    STATUS
                  </label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => {
                      setSelectedStatus(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full px-3 py-2 border border-white/15 rounded  text-white  text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All Statuses</option>
                    <option value="order_placed">Order Placed</option>
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="in_transit">In Transit</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                {/* Date From Filter */}
                <div>
                  <label className="block text-sm  font-semibold text-white/70 mb-2">
                    FROM DATE
                  </label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => {
                      setDateFrom(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full px-3 py-2 border border-white/15 rounded  text-white  text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                {/* Date To Filter */}
                <div>
                  <label className="block text-sm  font-semibold text-white/70 mb-2">
                    TO DATE
                  </label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => {
                      setDateTo(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full px-3 py-2 border border-white/15 rounded  text-white  text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Orders Table */}
        <div className=" rounded-lg shadow border border-white/[0.08]">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              <p className="mt-4 text-white/50 ">Loading orders...</p>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-white/50 ">No orders found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-transparent border-b border-white/[0.08]">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs  font-semibold text-white/70 uppercase tracking-wider">
                        Order #
                      </th>
                      <th className="px-6 py-3 text-left text-xs  font-semibold text-white/70 uppercase tracking-wider">
                        Merchant
                      </th>
                      <th className="px-6 py-3 text-left text-xs  font-semibold text-white/70 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-6 py-3 text-left text-xs  font-semibold text-white/70 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs  font-semibold text-white/70 uppercase tracking-wider">
                        Items
                      </th>
                      <th className="px-6 py-3 text-left text-xs  font-semibold text-white/70 uppercase tracking-wider">
                        Total
                      </th>
                      <th className="px-6 py-3 text-left text-xs  font-semibold text-white/70 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs  font-semibold text-white/70 uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className=" divide-y divide-white/[0.06]">
                    {filteredOrders.map((order) => (
                      <tr
                        key={order.order_id}
                        className="hover:bg-white/[0.03] transition-colors cursor-pointer"
                        onClick={() => navigate(`/provider/orders/${order.order_id}`)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className=" text-sm font-semibold text-white">
                            {order.order_number}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <p className="text-sm font-medium text-white">{order.merchant_shop_name}</p>
                            <p className="text-xs text-white/35">{order.merchant_shop_domain}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <p className="text-sm text-white">{order.customer_email}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white/50 ">
                          {formatDate(order.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white ">
                          {order.line_items_count}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-white ">
                          {formatCurrency(order.current_total_price_amount, order.currency_code)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(order)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/provider/orders/${order.order_id}`);
                            }}
                            className="text-orange-400 hover:text-primary-900  font-semibold"
                          >
                            View →
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-white/[0.08] flex items-center justify-between">
                  <div className="text-sm text-white/70 ">
                    Page {currentPage} of {totalPages}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-2 border border-white/15 rounded  text-white/70 hover:bg-white/[0.08] disabled:opacity-50 disabled:cursor-not-allowed  text-sm"
                    >
                      <ChevronLeftIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-2 border border-white/15 rounded  text-white/70 hover:bg-white/[0.08] disabled:opacity-50 disabled:cursor-not-allowed  text-sm"
                    >
                      <ChevronRightIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </ProviderLayout>
  );
};

export default ProviderOrdersList;
